import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { User } from 'firebase/auth';

export interface OnboardingStatus {
  isLoading: boolean;
  isNewUser: boolean;
  hasSeenSplash: boolean;
}

/**
 * Hook to check if a user has completed onboarding (seen the typewriter splash).
 * Returns loading state and whether the user is new.
 */
export function useOnboardingStatus(user: User | null): OnboardingStatus {
  const [isLoading, setIsLoading] = useState(true);
  const [hasSeenSplash, setHasSeenSplash] = useState(false);

  useEffect(() => {
    if (!user) {
      setIsLoading(true);
      return;
    }

    const checkOnboardingStatus = async () => {
      try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const data = userDoc.data();
          // User exists - check if they've seen the splash
          const seenSplash = data?.hasSeenSplash === true;
          setHasSeenSplash(seenSplash);
          console.log('👤 User onboarding status:', {
            uid: user.uid.substring(0, 8),
            hasSeenSplash: seenSplash,
            hasUserDoc: true,
          });
        } else {
          // New user - no document exists
          setHasSeenSplash(false);
          console.log('👤 New user detected:', {
            uid: user.uid.substring(0, 8),
            hasUserDoc: false,
          });
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        // On error, assume returning user to avoid blocking them
        setHasSeenSplash(true);
      } finally {
        setIsLoading(false);
      }
    };

    checkOnboardingStatus();
  }, [user]);

  return {
    isLoading,
    isNewUser: !hasSeenSplash,
    hasSeenSplash,
  };
}

/**
 * Mark user as having seen the splash screen.
 * Called when typewriter completes.
 */
export async function markSplashSeen(userId: string): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      hasSeenSplash: true,
      onboardedAt: new Date(),
    }, { merge: true });
    console.log('✅ User marked as onboarded:', userId.substring(0, 8));
  } catch (error) {
    console.error('Error marking splash as seen:', error);
    throw error;
  }
}











